// Stapel-Upload mit Tempo: das Backend begrenzt PUT /api/xf/assets/ (60 pro 60 s). Wir bleiben knapp darunter,
// warten bei Bedarf und versuchen es bei „429“ nach einer Pause erneut. Ohne DOM, damit sich das mit node prüfen lässt.

export interface UploadPacing {
  /** erlaubte Uploads pro Fenster (Server: 60, wir bleiben darunter) */
  limit: number;
  windowMs: number;
  /** Pause nach einer 429-Antwort */
  retryMs: number;
  maxRetries: number;
}

export const DEFAULT_PACING: UploadPacing = { limit: 50, windowMs: 60_000, retryMs: 10_000, maxRetries: 3 };

export interface QueueProgress {
  /** fertige Uploads */
  done: number;
  total: number;
  /** Datei, die gerade lädt oder auf ihren Start wartet */
  name: string;
  /** > 0: wartet noch so viele ms, bevor der Upload startet (Rate-Limit) */
  waitMs: number;
}

export interface QueueOptions<T> {
  upload(item: T): Promise<unknown>;
  nameOf(item: T): string;
  onProgress?(p: QueueProgress): void;
  /** Tempo; null = ohne Pause (z. B. Admins, die das Backend nicht begrenzt) */
  pacing?: UploadPacing | null;
  now?(): number;
  sleep?(ms: number): Promise<void>;
  /** true = nach dem aktuellen Eintrag abbrechen */
  cancelled?(): boolean;
}

export interface QueueResult<T> {
  done: number;
  /** Eintrag, an dem es endgültig scheiterte */
  failed?: { item: T; error: Error };
}

const realSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Wartezeit bis zum nächsten erlaubten Start, gegeben die Startzeiten der letzten Uploads */
export function waitFor(starts: number[], now: number, p: UploadPacing): number {
  const recent = starts.filter((s) => now - s < p.windowMs);
  if (recent.length < p.limit) return 0;
  return Math.max(0, recent[recent.length - p.limit] + p.windowMs - now);
}

export async function runUploads<T>(items: T[], opts: QueueOptions<T>): Promise<QueueResult<T>> {
  const now = opts.now || Date.now, sleep = opts.sleep || realSleep;
  const pacing = opts.pacing === undefined ? DEFAULT_PACING : opts.pacing;
  const starts: number[] = [];
  let done = 0;
  const report = (name: string, waitMs: number) => opts.onProgress?.({ done, total: items.length, name, waitMs });
  for (const item of items) {
    if (opts.cancelled?.()) break;
    const name = opts.nameOf(item);
    for (let attempt = 0; ; attempt++) {
      const wait = pacing ? waitFor(starts, now(), pacing) : 0;
      if (wait > 0) { report(name, wait); await sleep(wait); }
      report(name, 0);
      starts.push(now());
      try { await opts.upload(item); break; }
      catch (err) {
        const e = err as Error & { status?: number };
        if (e.status === 429 && pacing && attempt < pacing.maxRetries) { report(name, pacing.retryMs); await sleep(pacing.retryMs); continue; }
        return { done, failed: { item, error: e } };
      }
    }
    done++;
    report(name, 0);
  }
  return { done };
}
